import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Card, Nav, Accordion } from "react-bootstrap";

import { getGuruHistoryByTickerInfo, normalizeGuruHistory } from "../../apis/Dashboard";
import Avatar from "../../components/Avatar";
import PaginationControls from "../../components/PaginationControls";
import useDarkMode from "../../hooks/useDarkMode";
import { formatPercent } from "../../utils/formatters";
import { buildPortfolioGuruPath } from "../../utils/guruRoute";
import StockChartSection from "./StockChartSection";

const toHoldingTrade = (share) => ({
  date: share.date || null,
  numshare: share.numshare ?? null,
  totvalue: share.totvalue ?? null,
  transactioncode: share.transactioncode || null,
});

export default function InvestorListSection({
  ticker: propTicker,
  companykor,
  activeMainTab,
  onSelectionChange,
  disableNavigate = false,
}) {
  const { ticker: urlTicker } = useParams();
  const ticker = propTicker || urlTicker;
  const navigate = useNavigate();
  const [gurusInfo, setGurusInfo] = useState(null);
  const [isGuruLoading, setIsGuruLoading] = useState(false);
  const [guruStatus, setGuruStatus] = useState("buy");
  const [guruCategoryKey, setGuruCategoryKey] = useState("GURU");
  const [guruPage, setGuruPage] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const [expandedMobileKey, setExpandedMobileKey] = useState(null);
  const hasInitialized13FRef = useRef(false);
  const gurusPerPage = 5;

  const isDarkMode = useDarkMode();
  const hasGuruEntries = useCallback((categoryKey, status) => {
    const data = gurusInfo?.[categoryKey]?.[status];
    return Boolean(data && Object.keys(data).length > 0);
  }, [gurusInfo]);

  useEffect(() => {
    if (activeMainTab !== "13f" || hasInitialized13FRef.current || !gurusInfo) return;

    const priorities = [
      { categoryKey: "GURU", status: "buy" },
      { categoryKey: "GURU", status: "sell" },
      { categoryKey: "FUND", status: "buy" },
      { categoryKey: "FUND", status: "sell" },
    ];
    const nextSelection = priorities.find((priority) => hasGuruEntries(priority.categoryKey, priority.status))
      || { categoryKey: "GURU", status: "buy" };

    hasInitialized13FRef.current = true;
    setGuruCategoryKey(nextSelection.categoryKey);
    setGuruStatus(nextSelection.status);
  }, [activeMainTab, gurusInfo, hasGuruEntries]);

  useEffect(() => {
    if (!ticker) return;
    let isMounted = true;

    const loadGuruData = async () => {
      setIsGuruLoading(true);
      try {
        const response = await getGuruHistoryByTickerInfo(ticker);
        const data = await response.json();
        if (isMounted) {
          setGurusInfo(normalizeGuruHistory(data));
        }
      } catch (err) {
        console.error("Error fetching guru info:", err);
        if (isMounted) setGurusInfo(null);
      } finally {
        if (isMounted) setIsGuruLoading(false);
      }
    };

    loadGuruData();
    return () => { isMounted = false; };
  }, [ticker]);

  useEffect(() => {
    if (!ticker) return;
    setGuruPage(1);
    hasInitialized13FRef.current = false;
  }, [ticker]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setExpandedMobileKey(null);
  }, [ticker, activeMainTab, guruCategoryKey, guruStatus, guruPage, isMobile]);

  const filteredGurus = useMemo(() => {
    const guruCategory = gurusInfo?.[guruCategoryKey]?.[guruStatus] || gurusInfo?.[guruCategoryKey.toUpperCase()]?.[guruStatus];
    return guruCategory ? Object.values(guruCategory) : [];
  }, [gurusInfo, guruCategoryKey, guruStatus]);

  const totalGuruPages = Math.ceil(filteredGurus.length / gurusPerPage);
  const paginatedGurus = useMemo(() => {
    const start = (guruPage - 1) * gurusPerPage;
    return filteredGurus.slice(start, start + gurusPerPage);
  }, [filteredGurus, guruPage]);

  const handleStatusClick = useCallback((e, status) => {
    e.preventDefault();
    if (guruStatus !== status) {
      setGuruStatus(status);
      setGuruPage(1);
    }
  }, [guruStatus]);

  const handleCategoryClick = useCallback((e, category) => {
    e.preventDefault();
    if (guruCategoryKey !== category) {
      setGuruCategoryKey(category);
      setGuruPage(1);
    }
  }, [guruCategoryKey]);

  const handlePageChange = useCallback((page) => {
    setGuruPage(page);
  }, []);

  const emitSelection = useCallback((entry) => {
    const guru = entry?.guru || entry;
    const allShares = entry?.numShares || [];
    const selectedName = guru.leader || guru.nickname || guru.institution || null;

    if (!onSelectionChange) return;

    const trades = allShares
      .filter(Boolean)
      .map((share) => toHoldingTrade(share))
      .filter((trade) => trade.date);

    onSelectionChange({
      categoryKey: guruCategoryKey,
      status: guruStatus,
      selectedName,
      selectedTrades: trades,
      barTrades: [],
      isInsider: false,
      useAllInsiderTrades: false,
    });
  }, [guruCategoryKey, guruStatus, onSelectionChange]);

  const navigateToPortfolio = useCallback((guru) => {
    if (guruCategoryKey === "GURU") {
      navigate(buildPortfolioGuruPath("guru", guru));
      return;
    }

    if (guruCategoryKey === "FUND") {
      navigate(buildPortfolioGuruPath("fund", guru));
    }
  }, [guruCategoryKey, navigate]);

  const handleInvestorActivate = useCallback((entry, shouldNavigate = false) => {
    emitSelection(entry);
    if (!shouldNavigate) return;
    navigateToPortfolio(entry?.guru || entry);
  }, [emitSelection, navigateToPortfolio]);

  useEffect(() => {
    if (!onSelectionChange) return;
    if (!paginatedGurus.length) {
      onSelectionChange({
        categoryKey: guruCategoryKey,
        status: guruStatus,
        selectedName: null,
        selectedTrades: [],
        barTrades: [],
        isInsider: false,
        useAllInsiderTrades: false,
      });
      return;
    }
    handleInvestorActivate(paginatedGurus[0]);
  }, [
    onSelectionChange,
    paginatedGurus,
    handleInvestorActivate,
    guruCategoryKey,
    guruStatus,
  ]);

  if (activeMainTab && activeMainTab !== "13f") {
    return (
      <Card className="h-100 shadow-sm border-0" style={{ minHeight: "530px" }}>
        <Card.Body className="d-flex align-items-center justify-content-center">
          <p className="text-secondary m-0">해당 탭의 투자자 정보가 없습니다.</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="card-people h-100 shadow-sm border-0" style={{ minHeight: "530px" }}>
      <Card.Body className="h-100 d-flex flex-column">
        <Nav className="nav-line mb-4">
          <Nav.Link
            href="#"
            active={guruCategoryKey === "GURU"}
            onClick={(e) => handleCategoryClick(e, "GURU")}
          >
            투자대가
          </Nav.Link>
          <Nav.Link
            href="#"
            active={guruCategoryKey === "FUND"}
            onClick={(e) => handleCategoryClick(e, "FUND")}
          >
            미국연기금
          </Nav.Link>
        </Nav>
        <Nav className="nav-line mb-2 align-items-center">
          <Nav.Link
            href="#"
            active={guruStatus === "buy"}
            onClick={(e) => handleStatusClick(e, "buy")}
          >
            매수
          </Nav.Link>
          <Nav.Link
            href="#"
            active={guruStatus === "sell"}
            onClick={(e) => handleStatusClick(e, "sell")}
          >
            매도
          </Nav.Link>
          <Nav.Link
            href="#"
            className={isDarkMode ? "ms-auto active" : "ms-auto text-secondary active"}
            tabIndex={-1}
            style={{ color: isDarkMode ? "rgba(255, 255, 255, 0.75)" : undefined }}
          >
            보유비중
          </Nav.Link>
        </Nav>

        <ul className="people-group flex-grow-1 d-flex flex-column">
          {isGuruLoading ? (
            <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center text-muted" style={{ minHeight: "200px" }}>
              <div className="spinner-border spinner-border-sm text-primary mb-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div style={{ fontSize: "12px" }}>데이터를 불러오는 중입니다...</div>
            </div>
          ) : paginatedGurus.map((entry, index) => {
            const guru = entry?.guru || entry;
            const portfolioPath = buildPortfolioGuruPath(guruCategoryKey === "FUND" ? "fund" : "guru", guru);
            const itemKey = `${guruCategoryKey}-${guruStatus}-${guruPage}-${index}`;

            return (
              <React.Fragment key={itemKey}>
                <li
                  className="people-item d-flex align-items-center"
                  style={{ cursor: "pointer" }}
                  onMouseEnter={() => handleInvestorActivate(entry)}
                  onFocus={() => handleInvestorActivate(entry)}
                  onClick={() => {
                    handleInvestorActivate(entry, !disableNavigate);
                    if (isMobile && activeMainTab === "13f") {
                      setExpandedMobileKey(expandedMobileKey === itemKey ? null : itemKey);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleInvestorActivate(entry, !disableNavigate);
                      if (isMobile && activeMainTab === "13f") {
                        setExpandedMobileKey(expandedMobileKey === itemKey ? null : itemKey);
                      }
                    }
                  }}
                >
                  <div className="flex-shrink-0">
                    <Avatar
                      img={guru.imagefile ? `/images/webp/${guru.guruid}.webp` : undefined}
                    />
                  </div>
                  <div className="people-body flex-grow-1 px-2" style={{ minWidth: 0 }}>
                    <span className="d-block text-truncate" style={{ fontSize: "12px", marginBottom: "2px", color: isDarkMode ? "rgba(255, 255, 255, 0.7)" : undefined }}>{guru.nickname}</span>
                    <h6 className="m-0 text-truncate" style={{ fontSize: "14px", color: isDarkMode ? "#ffffff" : undefined }}>
                      <Link
                        to={portfolioPath}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleInvestorActivate(entry, !disableNavigate);
                          if (isMobile && activeMainTab === "13f") {
                            setExpandedMobileKey(expandedMobileKey === itemKey ? null : itemKey);
                          }
                        }}
                        className="text-truncate d-block"
                        style={{ color: isDarkMode ? "#ffffff" : undefined }}
                      >
                        {guru.leader}
                      </Link>
                    </h6>
                    <span className="d-block text-truncate" style={{ fontSize: "11px", color: isDarkMode ? "rgba(255, 255, 255, 0.6)" : undefined }}>{guru.institution}</span>
                  </div>
                  <div
                    className="flex-shrink-0"
                    style={{
                      minWidth: "60px",
                      textAlign: "right",
                      color: isDarkMode ? "rgba(255, 255, 255, 0.85)" : undefined,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                    }}
                  >
                    <span>
                      {formatPercent(guru.percent, { zeroAs: "0%", fallback: "0%" })}
                    </span>
                  </div>
                </li>
                {isMobile && activeMainTab === "13f" && expandedMobileKey === itemKey && (
                  <li className="list-unstyled mt-2">
                    <Accordion activeKey="0">
                      <Accordion.Item eventKey="0" className="border-0">
                        <Accordion.Body className="p-0">
                          <StockChartSection
                            ticker={ticker}
                            companykor={companykor}
                            chartSelection={entry ? {
                              categoryKey: guruCategoryKey,
                              status: guruStatus,
                              selectedName: guru.leader || guru.nickname || guru.institution || null,
                              selectedTrades: (entry?.numShares || [])
                                .filter(Boolean)
                                .map((share) => toHoldingTrade(share))
                                .filter((trade) => trade.date),
                              barTrades: [],
                              isInsider: false,
                              useAllInsiderTrades: false,
                            } : null}
                            activeMainTab={activeMainTab}
                          />
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </li>
                )}
              </React.Fragment>
            );
          })}
          {!filteredGurus.length && (
            <li className="people-item flex-grow-1 d-flex align-items-center justify-content-center text-center">
              <div className="people-body w-100">
                <span style={{ color: isDarkMode ? "rgba(255, 255, 255, 0.7)" : undefined }}>해당 상태의 데이터가 없습니다.</span>
              </div>
            </li>
          )}
        </ul>
        {filteredGurus.length > 0 && (
          <PaginationControls
            currentPage={guruPage}
            totalPages={totalGuruPages}
            onPrev={() => handlePageChange(guruPage - 1)}
            onNext={() => handlePageChange(guruPage + 1)}
            className="mt-2"
          />
        )}
      </Card.Body>
    </Card>
  );
}
